/**
 * Coinbase Decision Lab Panel — operator surface.
 * Rendered with portal MUI dark theme.
 */

import React, { useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  CoinbaseDecisionLabPayload,
  CoinbaseOrderHistoryPayload,
} from '../../services/coinbaseService';
import { getCoinbaseOrderHistory, clearCoinbaseKillSwitch } from '../../services/coinbaseService';
import CoinbaseIntelCharts from './CoinbaseIntelCharts';
import CoinbasePriceChart from './CoinbasePriceChart';

// ─── Formatters ──────────────────────────────────────────────────────────────

function usd(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function num(n: number | undefined, suffix = ''): string {
  if (n === undefined || Number.isNaN(n)) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

function pct(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

// ─── Color helpers ───────────────────────────────────────────────────────────

type ChipColor = 'success' | 'warning' | 'error' | 'info' | 'default';

function freshnessColor(v: string): ChipColor {
  return v === 'VALID' ? 'success' : v === 'DEGRADED' ? 'warning' : 'error';
}

function gateColor(g: string): ChipColor {
  return g === 'ALLOW' ? 'success' : g === 'PROBE_ONLY' ? 'info' : g === 'PAPER_ONLY' ? 'warning' : 'error';
}

function laneColor(lane: string): ChipColor {
  return lane.startsWith('INCOME') ? 'success' : lane.startsWith('PROBE') ? 'info' : lane.startsWith('ARM') ? 'warning' : 'default';
}

function apgeGradeColor(g: string): ChipColor {
  return g === 'A_PLUS' || g === 'A' ? 'success' : g === 'B' ? 'info' : g === 'F' ? 'error' : 'default';
}

function srcColor(s: string): ChipColor {
  return s === 'computed' ? 'success' : s === 'adapted' ? 'warning' : 'default';
}

function postureColor(p: string): ChipColor {
  return p === 'NORMAL' ? 'success' : p === 'SHUTDOWN' ? 'error' : 'warning';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontSize: '0.65rem', letterSpacing: '0.08em', display: 'block', lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ color: color ?? 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionCard({
  title,
  badge,
  children,
  accentColor,
}: {
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  accentColor?: string;
}) {
  return (
    <Card variant="outlined" sx={{ mb: 2, ...(accentColor && { borderColor: accentColor }) }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em' }}
          >
            {title}
          </Typography>
          {badge}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

const tableLabelSx = {
  color: 'text.secondary',
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontWeight: 600,
};

// ─── Main panel ──────────────────────────────────────────────────────────────

export default function CoinbaseDecisionLabPanel({ payload }: { payload: CoinbaseDecisionLabPayload }) {
  const theme = useTheme();
  const { engine, capital, market, risk, paper, verdict } = payload;
  const candidates = payload.candidates ?? [];
  const evaluations = payload.candidateEvaluations ?? [];
  const latestEval = (productId: string) => evaluations.find(e => e.productId === productId);
  const isLive = engine.mode === 'MICRO_LIVE' || engine.mode === 'INCOME_LIVE';
  const killSwitchActive = verdict.engineReady === false && verdict.dominantBlocker?.toLowerCase().includes('kill');

  const [orderHistory, setOrderHistory] = useState<CoinbaseOrderHistoryPayload | null>(null);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState<string | null>(null);
  const [killSwitchClearing, setKillSwitchClearing] = useState(false);
  const [killSwitchMsg, setKillSwitchMsg] = useState<string | null>(null);

  const loadOrderHistory = useCallback(async () => {
    setOrderHistoryLoading(true);
    setOrderHistoryError(null);
    try {
      const data = await getCoinbaseOrderHistory();
      setOrderHistory(data);
    } catch (err) {
      setOrderHistoryError((err as Error).message);
    } finally {
      setOrderHistoryLoading(false);
    }
  }, []);

  const handleClearKillSwitch = useCallback(async () => {
    setKillSwitchClearing(true);
    try {
      const result = await clearCoinbaseKillSwitch('manual operator clear from Decision Lab');
      setKillSwitchMsg(result.message);
    } catch (err) {
      setKillSwitchMsg(`Error: ${(err as Error).message}`);
    } finally {
      setKillSwitchClearing(false);
    }
  }, []);

  return (
    <Box>
      {/* Kill switch alert — shown when engine is halted */}
      {(risk.posture === 'SHUTDOWN' || killSwitchActive) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            isLive ? (
              <Button
                size="small"
                color="inherit"
                disabled={killSwitchClearing}
                onClick={() => { void handleClearKillSwitch(); }}
              >
                {killSwitchClearing ? 'Clearing…' : 'Clear Kill Switch'}
              </Button>
            ) : undefined
          }
        >
          <strong>Kill switch active — engine is SHUTDOWN.</strong>{' '}
          {verdict.dominantBlocker ?? 'No new orders will be placed until cleared.'}
          {killSwitchMsg && <Box sx={{ mt: 0.5, fontSize: '0.8rem' }}>{killSwitchMsg}</Box>}
        </Alert>
      )}

      {/* Balance strip — buying power vs CFM equity */}
      {orderHistory?.balance && (() => {
        const b = orderHistory.balance!;
        return (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setOrderHistory(null)}>
            <strong>Live balances:</strong>{' '}
            Buying power <strong>${parseFloat(b.futures_buying_power).toFixed(2)}</strong>{' '}
            · CFM ${parseFloat(b.cfm_usd_balance).toFixed(2)}{' '}
            · CBI ${parseFloat(b.cbi_usd_balance).toFixed(2)}{' '}
            · Margin used ${parseFloat(b.initial_margin).toFixed(2)}{' '}
            · Daily realized PnL{' '}
            <strong style={{ color: parseFloat(b.daily_realized_pnl) >= 0 ? '#4caf50' : '#f44336' }}>
              ${parseFloat(b.daily_realized_pnl).toFixed(2)}
            </strong>
          </Alert>
        );
      })()}

      {/* Instrument strip — compact live price overview */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {market.map(m => (
          <Card key={m.productId} variant="outlined" sx={{ flex: '1 1 280px', minWidth: 220 }}>
            <CardContent sx={{ py: '12px !important', px: '16px !important' }}>
              <CoinbasePriceChart productId={m.productId} mid={m.mid} />
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" label={m.freshnessVerdict} color={freshnessColor(m.freshnessVerdict)} variant="outlined" sx={{ fontSize: '0.62rem' }} />
                <Chip size="small" label={m.gateAction} color={gateColor(m.gateAction)} variant="outlined" sx={{ fontSize: '0.62rem' }} />
                {m.spreadBps !== undefined && (
                  <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center' }}>
                    {m.spreadBps.toFixed(1)} bps spread
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Verdict — full-width, visually dominant */}
      <SectionCard
        title="VERDICT"
        accentColor={verdict.dominantBlocker ? theme.palette.warning.main : theme.palette.success.main}
        badge={
          verdict.dominantBlocker
            ? <Chip size="small" label={verdict.dominantBlocker} color="warning" variant="outlined" />
            : <Chip size="small" label="SYSTEMS GO" color="success" variant="outlined" />
        }
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {verdict.headline}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {(
            [
              ['engine', verdict.engineReady],
              ['market', verdict.marketReady],
              ['capital', verdict.capitalReady],
              ['risk', verdict.riskReady],
              ['paper', verdict.paperReady],
            ] as [string, boolean][]
          ).map(([k, ok]) => (
            <Chip
              key={k}
              size="small"
              label={`${ok ? '✓' : '○'} ${k}`}
              color={ok ? 'success' : 'default'}
              variant={ok ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.7rem' }}
            />
          ))}
        </Box>
        {verdict.whyNotTrade && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', mr: 0.75 }}>
              Why not trade
            </Box>
            {verdict.whyNotTrade}
          </Typography>
        )}
        {verdict.whyNotBigger && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <Box component="span" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', mr: 0.75 }}>
              Why not bigger
            </Box>
            {verdict.whyNotBigger}
          </Typography>
        )}
        <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
          <Box component="span" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.08em', mr: 0.75 }}>
            Next
          </Box>
          {verdict.nextBestAction}
        </Typography>
      </SectionCard>

      {/* Engine + Capital — side by side */}
      <Grid container spacing={2} sx={{ mb: 0 }}>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="ENGINE"
            badge={
              <Chip
                size="small"
                label={engine.status}
                color={engine.status === 'LIVE' ? 'success' : engine.status === 'DEGRADED' ? 'warning' : engine.status === 'SHUTDOWN' ? 'error' : 'success'}
                variant={engine.status === 'LIVE' ? 'filled' : 'outlined'}
              />
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={6}><StatItem label="Mode" value={engine.mode} /></Grid>
              <Grid item xs={6}><StatItem label="Phase" value={engine.phase} /></Grid>
              <Grid item xs={6}>
                <StatItem
                  label="WS Status"
                  value={engine.wsHealth.status}
                  color={engine.wsHealth.status === 'SUBSCRIBED' ? theme.palette.success.main : theme.palette.error.main}
                />
              </Grid>
              <Grid item xs={6}><StatItem label="Reconnects" value={String(engine.wsHealth.reconnectCount)} /></Grid>
            </Grid>
            {engine.wsHealth.sequenceGap && (
              <Chip size="small" label="SEQ GAP" color="error" sx={{ mt: 1 }} />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {engine.products.join(' · ') || '—'}
            </Typography>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="CAPITAL FLYWHEEL"
            badge={
              <Chip
                size="small"
                label={capital.status}
                color={capital.status === 'ACTIVE' ? 'success' : 'warning'}
                variant="outlined"
              />
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatItem label="Active" value={usd(capital.activeTradingCapitalUsd)} color={theme.palette.success.main} />
              </Grid>
              <Grid item xs={6}><StatItem label="Base" value={usd(capital.capitalBaseUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Reserve" value={usd(capital.drawdownReserveUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Locked" value={usd(capital.lockedProfitUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Withdrawable" value={usd(capital.withdrawableProfitUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Allocated" value={usd(capital.allocatedFromTopstepUsd)} /></Grid>
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Market truth */}
      <SectionCard title="MARKET TRUTH">
        {market.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No products tracked.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableLabelSx}>Product</TableCell>
                <TableCell sx={tableLabelSx}>Freshness</TableCell>
                <TableCell sx={tableLabelSx}>Gate</TableCell>
                <TableCell sx={tableLabelSx}>Mid</TableCell>
                <TableCell sx={tableLabelSx}>Spread</TableCell>
                <TableCell sx={tableLabelSx}>Slippage</TableCell>
                <TableCell sx={tableLabelSx}>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {market.map(m => (
                <TableRow key={m.productId} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{m.productId}</TableCell>
                  <TableCell>
                    <Chip size="small" label={m.freshnessVerdict} color={freshnessColor(m.freshnessVerdict)} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.gateAction} color={gateColor(m.gateAction)} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(m.mid)}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(m.spreadBps, ' bps')}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(m.estimatedSlippageBps, ' bps')}</TableCell>
                  <TableCell>
                    {m.thinBook && <Chip size="small" label="thin" color="warning" sx={{ mr: 0.5, fontSize: '0.62rem' }} />}
                    {m.wideSpread && <Chip size="small" label="wide" color="warning" sx={{ mr: 0.5, fontSize: '0.62rem' }} />}
                    {m.crossedBook && <Chip size="small" label="crossed" color="error" sx={{ fontSize: '0.62rem' }} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* Signal charts */}
      <CoinbaseIntelCharts payload={payload} />

      {/* Paper forward */}
      {payload.paperForward && (() => {
        const pf = payload.paperForward!;
        const dom = Object.entries(pf.metrics.dominantBlockers).sort((a, b) => b[1] - a[1])[0];
        const cycleOutcomeColor = (o: string): ChipColor =>
          o === 'PAPER_ORDER_PLACED' ? 'success' : o === 'PAPER_EXIT' ? 'info' : o === 'BLOCKED' ? 'default' : 'warning';
        return (
          <SectionCard
            title="PAPER FORWARD"
            badge={
              <Chip size="small" label={pf.running ? 'Running' : 'Stopped'} color={pf.running ? 'success' : 'default'} variant="outlined" />
            }
          >
            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={6} sm={3}><StatItem label="Cycles today" value={String(pf.cyclesToday)} /></Grid>
              <Grid item xs={6} sm={3}><StatItem label="Nominated" value={String(pf.metrics.candidateNominatedCount)} /></Grid>
              <Grid item xs={6} sm={3}>
                <StatItem label="Paper orders" value={String(pf.metrics.paperOrderCount)} color={theme.palette.success.main} />
              </Grid>
              <Grid item xs={6} sm={3}><StatItem label="Blocked" value={String(pf.metrics.blockedCount)} /></Grid>
              <Grid item xs={6} sm={3}>
                <StatItem label="W / L / T" value={`${pf.metrics.paperWins} / ${pf.metrics.paperLosses} / ${pf.metrics.paperTimeStops}`} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatItem
                  label="Realized PnL"
                  value={usd(pf.metrics.realizedPnlUsd)}
                  color={pf.metrics.realizedPnlUsd >= 0 ? theme.palette.success.main : theme.palette.error.main}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatItem
                  label="Profit factor"
                  value={Number.isFinite(pf.metrics.profitFactor) ? pf.metrics.profitFactor.toFixed(2) : '∞'}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatItem label="Top blocker" value={dom ? `${dom[0]} (${dom[1]})` : '—'} />
              </Grid>
            </Grid>
            <Divider sx={{ my: 1.5 }} />
            {pf.recentCycles.slice(0, 8).map((c, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
                  borderBottom: i < Math.min(pf.recentCycles.length, 8) - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 68 }}>
                  {new Date(c.at).toLocaleTimeString()}
                </Typography>
                <Chip size="small" label={c.outcome} color={cycleOutcomeColor(c.outcome)} variant="outlined" sx={{ fontSize: '0.65rem', minWidth: 130 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>{c.oneLine}</Typography>
              </Box>
            ))}
          </SectionCard>
        );
      })()}

      {/* Intelligence bridge */}
      {(payload.intelligenceBridge ?? []).length > 0 && (
        <SectionCard title="INTELLIGENCE BRIDGE">
          {(payload.intelligenceBridge ?? []).map(b => {
            const mods: [string, string][] = [
              ['Tomi', b.tomiSource], ['Kaze', b.kazeSource], ['Chart', b.chartSource],
              ['APGE', b.apgeSource], ['Greeks', b.greekSource], ['LogFib', b.logFibSource],
            ];
            return (
              <Box key={b.productId} sx={{ py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { border: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{b.productId}</Typography>
                  <Chip size="small" label={`APGE ${b.apgeGrade}`} color={apgeGradeColor(b.apgeGrade)} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  <Chip size="small" label={b.readyForPaperCandidate ? 'ready' : 'not ready'} color={b.readyForPaperCandidate ? 'success' : 'default'} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  <Chip size="small" label={isLive ? 'LIVE' : 'paper-safe'} color={isLive ? 'success' : 'default'} variant={isLive ? 'filled' : 'outlined'} sx={{ fontSize: '0.65rem' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {mods.map(([k, s]) => (
                    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{k}</Typography>
                      <Chip size="small" label={s} color={srcColor(s)} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                    </Box>
                  ))}
                </Box>
                {b.blockers.length > 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                    {b.blockers.join(' · ')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </SectionCard>
      )}

      {/* Signal Scan — per-product intelligence from live runner */}
      <SectionCard
        title="SIGNAL SCAN"
        badge={<Chip size="small" label={isLive ? 'LIVE' : 'observe'} color={isLive ? 'success' : 'default'} variant={isLive ? 'filled' : 'outlined'} sx={{ fontSize: '0.62rem' }} />}
      >
        {payload.intelligence.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No intelligence data yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {payload.intelligence.map(intel => {
              const lastEntry = payload.paper.latestJournal.find(j => j.productId === intel.productId);
              return (
                <Box key={intel.productId} sx={{ p: 1.25, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 160 }}>{intel.productId}</Typography>
                    {intel.apge?.grade && (
                      <Chip size="small" label={`APGE ${intel.apge.grade}`} color={apgeGradeColor(intel.apge.grade)} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    )}
                    {intel.apge?.action && (
                      <Chip size="small" label={intel.apge.action} color="default" variant="outlined" sx={{ fontSize: '0.62rem' }} />
                    )}
                    {lastEntry?.dominantBlocker && (
                      <Chip size="small" label={lastEntry.dominantBlocker} color="warning" variant="outlined" sx={{ fontSize: '0.62rem' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {intel.tomiScore !== undefined && (
                      <Box>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>TOMI</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: intel.tomiScore >= 0.6 ? theme.palette.success.main : intel.tomiScore >= 0.4 ? theme.palette.warning.main : theme.palette.error.main }}>
                          {intel.tomiScore.toFixed(3)}
                        </Typography>
                      </Box>
                    )}
                    {intel.kazeScore !== undefined && (
                      <Box>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>KAZE</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: intel.kazeScore >= 0.6 ? theme.palette.success.main : intel.kazeScore >= 0.4 ? theme.palette.warning.main : theme.palette.error.main }}>
                          {intel.kazeScore.toFixed(3)}
                        </Typography>
                      </Box>
                    )}
                    {intel.flow && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>DOM</Typography>
                          <Chip size="small" label={intel.flow.domStatus} color={intel.flow.domStatus === 'confirmed' ? 'success' : intel.flow.domStatus === 'partial' ? 'warning' : 'default'} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>TAPE</Typography>
                          <Chip size="small" label={intel.flow.tapeStatus} color={intel.flow.tapeStatus === 'confirmed' ? 'success' : intel.flow.tapeStatus === 'hostile' ? 'error' : 'warning'} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>COMBINED</Typography>
                          <Chip size="small" label={intel.flow.combinedStatus} color={intel.flow.combinedStatus === 'confirmed' ? 'success' : intel.flow.combinedStatus === 'hostile' ? 'error' : 'warning'} variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        </Box>
                      </>
                    )}
                    {lastEntry?.strategyClass && (
                      <Box>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>STRATEGY</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{lastEntry.strategyClass}</Typography>
                      </Box>
                    )}
                  </Box>
                  {intel.flow?.reasons && intel.flow.reasons.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.63rem' }}>
                      {intel.flow.reasons.join(' · ')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </SectionCard>

      {/* Risk + Paper journal */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <SectionCard
            title="RISK"
            badge={<Chip size="small" label={risk.posture} color={postureColor(risk.posture)} variant="outlined" />}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}><StatItem label="Daily PnL" value={usd(risk.dailyPnlUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="24h PnL" value={usd(risk.rolling24hPnlUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Drawdown" value={usd(risk.drawdownUsd)} /></Grid>
              <Grid item xs={6}><StatItem label="Margin" value={pct(risk.marginUsagePct)} /></Grid>
              <Grid item xs={6}><StatItem label="Liq distance" value={pct(risk.liquidationDistancePct)} /></Grid>
              <Grid item xs={6}><StatItem label="Size cap" value={risk.dominantRiskCap ?? '—'} /></Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard
            title={isLive ? 'DECISION LOG' : 'PAPER DECISIONS'}
            badge={
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary">realized {usd(paper.realizedPnlUsd)}</Typography>
                <Typography variant="caption" color="text.secondary">unrealized {usd(paper.unrealizedPnlUsd)}</Typography>
              </Box>
            }
          >
            {paper.latestJournal.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No decisions yet.</Typography>
            ) : (
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr auto auto auto auto', gap: '0 8px', mb: 0.5, px: 0.5 }}>
                  {['TIME', 'PRODUCT', 'ACTION', 'APGE', 'TOMI', 'KAZE'].map(h => (
                    <Typography key={h} variant="overline" sx={{ fontSize: '0.58rem', color: 'text.disabled', letterSpacing: '0.06em', lineHeight: 1.8 }}>{h}</Typography>
                  ))}
                </Box>
                {paper.latestJournal.map((j, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '64px 1fr auto auto auto auto',
                      gap: '0 8px',
                      alignItems: 'center',
                      py: 0.5,
                      px: 0.5,
                      borderBottom: i < paper.latestJournal.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>
                      {new Date(j.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3, fontSize: '0.7rem' }} noWrap>
                        {j.productId}
                      </Typography>
                      {j.dominantBlocker && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', fontFamily: 'monospace' }} noWrap>
                          {j.dominantBlocker}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={j.finalAction}
                      color={
                        j.finalAction === 'LIVE_ORDER_PLACED' ? 'success' :
                        j.finalAction === 'PAPER_ORDER_PLACED' ? 'info' :
                        j.finalAction === 'OBSERVED' ? 'info' : 'default'
                      }
                      variant={j.finalAction === 'LIVE_ORDER_PLACED' || j.finalAction === 'PAPER_ORDER_PLACED' ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.6rem', height: 20 }}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      {j.apgeGrade ? (
                        <Chip size="small" label={j.apgeGrade} color={apgeGradeColor(j.apgeGrade)} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </Box>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right', fontSize: '0.65rem', color: j.tomiScore !== undefined && j.tomiScore >= 0.6 ? 'success.main' : j.tomiScore !== undefined && j.tomiScore >= 0.4 ? 'warning.main' : 'text.secondary' }}>
                      {j.tomiScore !== undefined ? j.tomiScore.toFixed(2) : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right', fontSize: '0.65rem', color: j.kazeScore !== undefined && j.kazeScore >= 0.6 ? 'success.main' : j.kazeScore !== undefined && j.kazeScore >= 0.4 ? 'warning.main' : 'text.secondary' }}>
                      {j.kazeScore !== undefined ? j.kazeScore.toFixed(2) : '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* ─── DeFi Treasury ─────────────────────────────────────── */}
      {payload.defi && (() => {
        const d = payload.defi!;
        const alloc = d.allocation;
        const laneColor = (lane: string): 'success' | 'warning' | 'error' | 'default' => {
          if (lane === 'LEND_USDC') return 'success';
          if (lane === 'BORROW_USDC') return 'warning';
          if (lane === 'EMERGENCY_EXIT') return 'error';
          return 'default';
        };
        return (
          <SectionCard
            title="DeFi Treasury"
            badge={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={alloc.actionLane}
                  color={laneColor(alloc.actionLane)}
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', fontWeight: 700 }}
                />
                {d.stale && (
                  <Chip size="small" label={`stale ${Math.floor(d.staleSinceMs / 1000)}s`} color="warning" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                )}
              </Box>
            }
          >
            {/* Allocation stats row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
              {[
                { label: 'Phase', val: `${alloc.phase}` },
                { label: 'Active', val: `$${alloc.activeTradingUsd.toFixed(0)}` },
                { label: 'Reserve', val: `$${alloc.reserveUsd.toFixed(0)}` },
                { label: 'DeFi Target', val: `$${alloc.defiTargetUsd.toFixed(0)}` },
                { label: 'Headroom', val: `$${alloc.defiHeadroomUsd.toFixed(0)}` },
                { label: 'Best APY', val: `${d.bestSupplyApyPct.toFixed(2)}%` },
                { label: 'Rec. Lend', val: `$${alloc.recommendedLendAmountUsd.toFixed(0)}` },
              ].map(({ label, val }) => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{val}</Typography>
                </Box>
              ))}
            </Box>

            {/* Borrow gates */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
              {alloc.borrowGates.map(g => (
                <Chip
                  key={g.gate}
                  size="small"
                  label={`${g.pass ? '✓' : '○'} ${g.gate}`}
                  color={g.pass ? 'success' : 'default'}
                  variant={g.pass ? 'filled' : 'outlined'}
                  title={g.detail}
                  sx={{ fontSize: '0.6rem' }}
                />
              ))}
            </Box>

            {/* Protocol table */}
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  {['Protocol', 'Supply APY', 'Borrow APY', 'Utilization', 'Liquidity', 'Source'].map(h => (
                    <TableCell key={h} sx={{ py: 0.25, fontSize: '0.6rem', color: 'text.disabled' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {d.pools.map(p => (
                  <TableRow
                    key={p.protocolId}
                    sx={{
                      backgroundColor: p.protocolId === d.bestSupplyProtocol
                        ? 'rgba(76,175,80,0.06)' : 'transparent',
                    }}
                  >
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: p.protocolId === d.bestSupplyProtocol ? 700 : 400 }}>
                      {p.protocolId}
                    </TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontFamily: 'monospace' }}>{p.supplyApyPct.toFixed(2)}%</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontFamily: 'monospace' }}>{p.variableBorrowApyPct.toFixed(2)}%</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontFamily: 'monospace' }}>{p.utilizationRatePct.toFixed(1)}%</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                      {p.availableLiquidityUsd >= 1_000_000
                        ? `$${(p.availableLiquidityUsd / 1_000_000).toFixed(1)}M`
                        : `$${(p.availableLiquidityUsd / 1_000).toFixed(0)}K`}
                    </TableCell>
                    <TableCell sx={{ py: 0.25 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={p.source}
                          color={p.source === 'on-chain' ? 'success' : p.source === 'defillama' ? 'info' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.55rem', height: 16 }}
                        />
                        {p.stale && <Chip size="small" label="stale" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Reasoning */}
            {alloc.reasoning.slice(0, 3).map((r, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                {r}
              </Typography>
            ))}

            {/* Warnings */}
            {d.warnings.length > 0 && (
              <Box sx={{ mt: 0.75 }}>
                {d.warnings.map((w, i) => (
                  <Typography key={i} variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    ⚠ {w}
                  </Typography>
                ))}
              </Box>
            )}
          </SectionCard>
        );
      })()}

      {/* Order history — on-demand fetch from exchange */}
      <SectionCard title="Order History" badge={
        <Button
          size="small"
          variant="outlined"
          disabled={orderHistoryLoading}
          onClick={() => { void loadOrderHistory(); }}
          sx={{ fontSize: '0.62rem', py: 0, px: 1, height: 22 }}
        >
          {orderHistoryLoading ? 'Loading…' : 'Fetch from Exchange'}
        </Button>
      }>
        {orderHistoryError && (
          <Typography variant="caption" color="error">{orderHistoryError}</Typography>
        )}
        {orderHistory && (
          <>
            {/* Open positions */}
            {orderHistory.positions.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Open Positions
                </Typography>
                {orderHistory.positions.map(pos => (
                  <Box key={pos.product_id} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip size="small" label={pos.side} color={pos.side === 'LONG' ? 'success' : 'error'} sx={{ fontSize: '0.6rem' }} />
                    <Typography variant="caption">{pos.product_id}</Typography>
                    <Typography variant="caption">{pos.number_of_contracts} contracts @ ${parseFloat(pos.avg_entry_price).toFixed(2)}</Typography>
                    <Typography variant="caption" color={parseFloat(pos.unrealized_pnl) >= 0 ? 'success.main' : 'error.main'}>
                      uPnL ${parseFloat(pos.unrealized_pnl).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {/* Recent orders */}
            {orderHistory.orders.length === 0 ? (
              <Typography variant="caption" color="text.disabled">No recent orders.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Time</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Product</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Side</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Size</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Avg Price</TableCell>
                    <TableCell sx={{ fontSize: '0.62rem', py: 0.5 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderHistory.orders.slice(0, 20).map(o => (
                    <TableRow key={o.order_id}>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>
                        {new Date(o.created_time).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>{o.product_id}</TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>
                        <Chip
                          size="small"
                          label={o.side}
                          color={o.side === 'BUY' ? 'success' : 'error'}
                          sx={{ fontSize: '0.55rem', height: 16 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>{o.filled_size}</TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>
                        {parseFloat(o.average_filled_price) > 0 ? `$${parseFloat(o.average_filled_price).toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', py: 0.25 }}>{o.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
        {!orderHistory && !orderHistoryLoading && !orderHistoryError && (
          <Typography variant="caption" color="text.disabled">
            Click "Fetch from Exchange" to load live order history, positions, and balances.
          </Typography>
        )}
      </SectionCard>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
        {new Date(payload.timestamp).toLocaleString()}
      </Typography>
    </Box>
  );
}
